import * as React from "react"
import { cn } from "../../lib/utils"

const valueToDigits = (val) =>
  String(Math.round(Math.abs(val || 0) * 100))

const formatDigits = (d) => {
  const padded = d.padStart(3, '0')
  let intPart = padded.slice(0, -2).replace(/^0+/, '') || '0'
  const decPart = padded.slice(-2)
  const formattedInt = parseInt(intPart, 10).toLocaleString('pt-BR')
  return `${formattedInt},${decPart}`
}

const CurrencyInput = React.forwardRef(({
  value = 0,
  onChange,
  placeholder = "0,00",
  className,
  disabled,
  id,
  ...props
}, forwardedRef) => {
  const inputRef = React.useRef(null)
  const cursorRef = React.useRef(null)
  const isInternalRef = React.useRef(false)
  const internalValueRef = React.useRef(value)

  const controlled = typeof onChange === 'function'
  const currentValue = controlled ? value : internalValueRef.current

  React.useImperativeHandle(forwardedRef, () => ({
    getValue: () => internalValueRef.current
  }), [])

  const [displayValue, setDisplayValue] = React.useState(() =>
    formatDigits(valueToDigits(currentValue))
  )

  React.useEffect(() => {
    if (isInternalRef.current) {
      isInternalRef.current = false
      return
    }
    const newDigits = valueToDigits(value)
    setDisplayValue(formatDigits(newDigits))
    internalValueRef.current = value
  }, [value])

  React.useEffect(() => {
    if (cursorRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorRef.current, cursorRef.current)
      cursorRef.current = null
    }
  })

  const handleChange = (e) => {
    const raw = e.target.value
    const cursor = e.target.selectionStart
    const digitsBefore = raw.slice(0, cursor).replace(/\D/g, '').length
    const newDigits = raw.replace(/\D/g, '')

    if (newDigits === valueToDigits(currentValue)) return

    const formatted = formatDigits(newDigits || '0')
    setDisplayValue(formatted)

    const oldDigitsLen = valueToDigits(currentValue).length

    if (newDigits.length < oldDigitsLen) {
      cursorRef.current = formatted.length
    } else {
      let pos = 0
      let count = 0
      for (let i = 0; i < formatted.length && count < digitsBefore; i++) {
        pos = i + 1
        if (/\d/.test(formatted[i])) count++
      }
      cursorRef.current = pos
    }

    const cents = parseInt(newDigits || '0', 10)
    const numericValue = cents / 100
    if (numericValue !== currentValue) {
      internalValueRef.current = numericValue
      if (controlled) {
        isInternalRef.current = true
        onChange(numericValue)
      }
    }
  }

  return (
    <input
      ref={inputRef}
      type="text"
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      id={id}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm currency-input",
        className
      )}
      {...props}
    />
  )
})
CurrencyInput.displayName = "CurrencyInput"

export { CurrencyInput }
